import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { UserMeResponse, UpdateAdminUserPayload } from '../models/user-me.models';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly apiUrl = `${environment.apiUrl}/data/user/me`;

  readonly profileUpdated$ = new Subject<void>();

  constructor(private readonly router: Router) {}

  private cachedUserData: UserMeResponse | null = null;
  private userPromise: Promise<UserMeResponse | null> | null = null;

async getUserMe(email: string, forceRefresh = false): Promise<UserMeResponse | null> {
  // 1. Reset se l'email è cambiata
  if (this.cachedUserData && this.cachedUserData.email !== email) {
    this.clearCache();
  }

  // 2. Ritorna cache se valida
  if (this.cachedUserData && !forceRefresh) {
    return this.cachedUserData;
  }

  // 3. Gestione della Promise per evitare chiamate multiple simultanee
  if (this.userPromise && !forceRefresh) {
    return this.userPromise;
  }

  // Creiamo la promise
  this.userPromise = (async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const url = `${this.apiUrl}?email=${encodeURIComponent(email)}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        if (response.status === 404 || body?.error) {
          await this.handleUserNotFound();
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data?.error) {
        await this.handleUserNotFound();
        return null;
      }
      this.cachedUserData = data;
      return data;
    } catch (error: any) {
      console.error("Errore getUserMe:", error);
      return null;
    } finally {
      // IMPORTANTE: Puliamo la promise solo DOPO un po' o lasciamola null
      // per permettere tentativi successivi, ma non istantanei
      this.userPromise = null; 
    }
  })();

  return this.userPromise;
}

  private readonly adminUsersUrl = `${environment.apiUrl}/admin/users`;

  /**
   * Fetches admin users based on domain prefixes.
   * This is a POST request as it requires a JSON body.
   */
  async getAdminUsers(payload: { domain_prefixes: string[] }): Promise<any[]> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${environment.apiUrl}/admin/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Failed to fetch admin users');
    
    const data = await response.json();
    return data.users || []; 
  }

  /**
   * Updates an admin user with new information (name, status, gender, age, etc.)
   * PUT request to /admin/users
   * MODIFICA 2: Ora supporta anche gender e age
   */
  async updateAdminUsers(payload: UpdateAdminUserPayload): Promise<any> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${environment.apiUrl}/admin/users`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Failed to update admin user');
    
    const data = await response.json();
    return data;
  }

  /**
   * Creates a new user associated with a domain
   * POST request to /admin/users/domains
   * MODIFICA 3: Supporta la creazione di utenti con tutti i campi
   */
  async createUser(payload: { 
    email: string; 
    domain_prefix: string; 
    role_code: string; 
    first_name: string; 
    last_name: string; 
  }): Promise<any> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const url = `${environment.apiUrl}/admin/users/domains`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.text();
        const err = new Error(`Errore nella creazione dell'utente: ${response.status} - ${errorBody}`);
        (err as any).status = response.status;
        throw err;
      }

      return await response.json();

    } catch (error) {
      console.error('Service Create User Error:', error);
      throw error;
    }
  }

  /**
   * Updates the user-domain relationship (role assignment)
   * PUT request to /admin/users/domains
   * MODIFICA 3: Questa è la PRIMA API da chiamare nell'update sequenziale
   */
  async updateUserDomain(payload: { email: string; domain_prefix: string; role_code: string; }): Promise<any> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const url = `${environment.apiUrl}/admin/users/domains`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Errore nell'aggiornamento della relazione utente-dominio: ${response.status} - ${errorBody}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Service Update User Domain Error:', error);
      throw error;
    }
  }

  /**
   * Deletes a user from a domain
   * DELETE request to /admin/users/domains
   */
  async deleteUser(payload: { email: string; domain_prefix: string }): Promise<void> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const url = `${environment.apiUrl}/admin/users/domains`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Errore nell'eliminazione dell'utente: ${response.status} - ${errorBody}`);
      }

    } catch (error) {
      console.error('Service Delete User Error:', error);
      throw error;
    }
  }

  clearCache() {
    this.cachedUserData = null;
    this.userPromise = null;
    console.log('[UserService] Cache pulita correttamente');
  }

  notifyProfileUpdated(): void {
    this.profileUpdated$.next();
  }

  private async handleUserNotFound(): Promise<void> {
    this.clearCache();
    try { await signOut(); } catch { /* ignora errori di signOut */ }
    this.router.navigate(['/auth/login']);
  }

/**
   * Fetches the domains associated with a specific user email
   * GET request to /admin/users/domain with Pagination & Search
   */
/**
   * Fetches the domains associated with a specific user email
   * GET request to /admin/users/domain with Pagination & Search
   */
  async getUserDomains(email: string, page: number = 1, pageSize: number = 10, search: string = ''): Promise<any> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      // Costruiamo la query string includendo SEMPRE l'email
      const params = new URLSearchParams({
        
        page: page.toString(),
        page_size: pageSize.toString()
      });

      // Aggiungiamo il search solo se l'utente ha digitato qualcosa
      if (search && search.trim() !== '') {
        params.append('search', search.trim());
      }
      
      // URL finale completo
      const url = `${environment.apiUrl}/data/user/me/domains?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Errore nel recupero dei domini: ${response.status} - ${errorBody}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Service Get User Domains Error:', error);
      throw error;
    }
  }
}